import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { baseApi } from "./baseApi";

export const useGetTrendingSymbols = (type?: string, broker_id?: string) => {
  return useQuery({
    queryKey: ["get-trending-symbols", type, broker_id],
    queryFn: !type
      ? skipToken
      : async () => {
        const response = await baseApi.get(
          `/get_search_items_suggestions_v2?type=${type}&broker_id=${broker_id ?? "4"}`,
        );
        return response;
      },
    retry: 0,
    staleTime: 3000,
  });
};

export const useGetMatchSymbol = (type?: string) => {
  return useQuery({
    queryKey: ["get_matchtrader_symbols", type],
    queryFn: !type
      ? skipToken
      : async () => {
        const response = await baseApi.get(
          `/get_matchtrader_symbols?type=${type}`,
        );
        return response;
      },
    retry: 0,
    staleTime: 3000,
  });
};

export const useGetCtraderSymbol = (user_id?: string) => {
  return useQuery({
    queryKey: ["get_ctrader_symbols", user_id],
    queryFn: !user_id
      ? skipToken
      : async () => {
        const response = await baseApi.get(
          `/get_ctrader_instruments?user_id=${user_id}`,
        );
        return response;
      },
    retry: 0,
    staleTime: 3000,
  });
};
export const useGetLeverateSymbol = (user_id?: string, broker?: string) => {
  return useQuery({
    queryKey: ["get_leverate_instruments", user_id, broker],
    queryFn: !user_id
      ? skipToken
      : async () => {
        const response = await baseApi.get(
          `/get_leverate_instruments?user_id=${user_id}`,
        );
        return response;
      },
    enabled: !!user_id && !!broker,
    retry: 0,
    staleTime: 3000,
  });
};

export const useGetInstrumentSuggestion = (
  userId?: string,
  type?: string,
  broker?: string,
) => {
  return useQuery({
    queryKey: ["get-instrument-suggestions", userId, type, broker],
    queryFn: !userId
      ? skipToken
      : async () => {
        const response = await baseApi.get(
          `/get_instrument_suggestions_v2?instrument=${"val"}&user_id=${userId}&type=${type ?? "equity"
          }&broker_id=${broker ?? "4"}`,
        );
        return response;
      },
    retry: 0,
    staleTime: 3000,
  });
};

export const useGetTickerSnapshot = (symbol?: string) => {
  return useQuery({
    queryKey: ["get-tickers-snapshot", symbol],
    queryFn: !symbol
      ? skipToken
      : async () => {
        const response = await baseApi.get(
          `/get_tickers_snapshot?tickers=${symbol}`,
        );
        return response;
      },
    retry: 0,
    staleTime: 3000,
  });
};

export const useGetTickerData = () => {
  return useMutation({
    mutationKey: ["get_ticker_data"],
    mutationFn: async ({
      ticker,
      timestamp,
      strategy_id,
      user_id,
      broker_id,
      request_type,
      start_date,
      end_date,
    }: {
      ticker: string;
      timestamp: string;
      strategy_id?: string | number;
      user_id: string;
      broker_id?: string;
      request_type?: string;
      start_date?: string;
      end_date?: string;
    }) => {
      const queryParams = new URLSearchParams({
        ticker,
        timestamp,
        user_id,
      });

      if (strategy_id !== undefined && strategy_id !== null) {
        queryParams.append("strategy_id", strategy_id.toString());
      }
      if (broker_id !== undefined && broker_id !== null) {
        queryParams.append("broker_id", broker_id.toString());
      }
      queryParams.append("limit", "15000");
      if (request_type !== undefined && request_type !== null) {
        queryParams.append("request_type", request_type.toString());
      }
      if (start_date) {
        queryParams.append("start", start_date);
      }
      if (end_date) {
        queryParams.append("end_date", end_date);
      }

      const response = await baseApi.get(
        `/get_ticker_data_v2?${queryParams.toString()}`,
      );

      return response;
    },
  });
};

export const useGetForwardTestMetrics = () => {
  return useMutation({
    mutationKey: ["forward_test_metrics"],
    mutationFn: async ({
      user_id,
      deployment_id,
    }: {
      user_id: string;
      deployment_id: string;
    }) => {
      const response = await baseApi.get(
        `/forward_test_metrics?user_id=${user_id}&deployment_id=${deployment_id}`,
      );
      return response;
    },
  });
};
export const useGetSymbolData = () => {
  return useMutation({
    mutationKey: ["get_symbol_data_tickers_v3"],
    mutationFn: async ({
      ticker,
      user_id,
      broker_id,
    }: {
      ticker: string;
      user_id: string;
      broker_id?: string;
    }) => {
      const response = await baseApi.get("/get_symbol_data_tickers_v3", {
        params: {
          ticker,
          user_id,
          ...(broker_id && { broker_id }),
        },
      });

      if (Object.keys(response?.data ?? {}).length === 1) {
        return { data: response.data[ticker] };
      }

      return response;
    },
    retryDelay: 2000,
  });
};

export const useGetSymbolDataTicker = (
  ticker: string,
  user_id: string,
  broker_id?: string,
  polling: boolean = false,
) => {
  return useQuery({
    queryKey: ["get_symbol_data_tickers_v3", ticker, user_id, broker_id],
    queryFn: async () => {
      const response = await baseApi.get("/get_symbol_data_tickers_v3", {
        params: {
          ticker,
          user_id,
          ...(broker_id && { broker_id }),
        },
      });

      if (Object.keys(response?.data ?? {}).length === 1) {
        return response.data[ticker];
      }

      return response.data;
    },
    enabled: !!ticker && !!user_id,
    refetchInterval: polling ? 5000 : false,
  });
};

export const useGetSymbolsData = () => {
  return useMutation({
    mutationKey: ["get_symbol_data_tickers_v3"],
    mutationFn: async ({
      ticker,
      user_id,
      broker_id,
    }: {
      ticker: string;
      user_id: string;
      broker_id: string;
    }) => {
      const response = await baseApi.get(
        `/get_symbol_data_tickers_v3?ticker=${ticker}&user_id=${user_id}&broker_id=${broker_id}`,
      );

      return response;
    },
    retryDelay: 2000,
  });
};

export const useGetSwapValues = () => {
  return useMutation({
    mutationKey: ["get_swap_values"],
    mutationFn: async ({
      user_id,
      strategy_id,
      deployment_id,
    }: {
      user_id: string;
      strategy_id: string;
      deployment_id: string;
    }) => {
      const response = await baseApi.get(
        `/get_swap_values?user_id=${user_id}&strategy_id=${strategy_id}&deployment_id=${deployment_id}`,
      );
      return response;
    },
    retryDelay: 2000,
  });
};
